import { ChevronDown, Search } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useCountriesQuery } from '@/hooks/business/useCountriesQuery'
import { Country } from '@/interfaces/billing'
import { cn } from '@/lib/utils'

interface CountrySelectorProps {
  value?: string // Can be country code, name, or prefix depending on valueType
  onChange: (_country: Country) => void
  placeholder?: string
  disabled?: boolean
  showFlag?: boolean
  showPrefix?: boolean
  searchPlaceholder?: string
  className?: string
  valueType?: 'code' | 'name' | 'prefix' // What property to use as value
  triggerClassName?: string
  contentClassName?: string
  compactMode?: boolean // Show only flag + prefix for phone selector
}

export const CountrySelector: FC<CountrySelectorProps> = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  showFlag = true,
  showPrefix = false,
  searchPlaceholder = '',
  className = '',
  valueType = 'name',
  triggerClassName = '',
  contentClassName = '',
  compactMode = false,
}) => {
  const { countries, isLoadingCountries } = useCountriesQuery({})
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [displayCount, setDisplayCount] = useState(20)

  // Helper function to render flag with proper Windows/Chromium support
  const renderFlag = useCallback((flag: string, countryCode: string) => {
    // Generate proper Unicode flag emoji from country code
    const getUnicodeFlag = (code: string) => {
      if (code.length !== 2) return code
      try {
        const codePoints = Array.from(code.toUpperCase()).map(
          (char) => 127397 + char.charCodeAt(0)
        )
        return String.fromCodePoint(...codePoints)
      } catch {
        return code
      }
    }

    const unicodeFlag = getUnicodeFlag(countryCode)

    return (
      <span
        className="inline-flex items-center justify-center text-lg leading-none"
        data-country-code={countryCode}
        style={{
          fontFamily:
            '"Twemoji Country Flags", "Apple Color Emoji", "Segoe UI Emoji", ' +
            '"Noto Color Emoji", "EmojiOne Mozilla", system-ui, sans-serif',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '1.5em',
          minHeight: '1.5em',
          textAlign: 'center',
          fontSize: '1.1rem',
          lineHeight: '1',
          verticalAlign: 'middle',
          fontVariantEmoji: 'emoji',
        }}
        title={`${countryCode} flag`}
      >
        {unicodeFlag}
      </span>
    )
  }, [])

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return countries.slice(0, displayCount)

    const query = searchQuery.toLowerCase()
    return countries
      .filter(
        (option) =>
          option.name.toLowerCase().includes(query) ||
          option.prefix.includes(query) ||
          option.code.toLowerCase().includes(query)
      )
      .slice(0, displayCount)
  }, [countries, searchQuery, displayCount])

  // Get current selected option
  const selectedOption = useMemo(() => {
    if (!value) {
      // Default to Cuba if no value provided
      return countries.find((opt) => opt.code === 'CU') || countries[0]
    }

    // Find option based on valueType
    switch (valueType) {
      case 'code':
        return countries.find((opt) => opt.code === value)
      case 'prefix':
        return countries.find((opt) => opt.prefix === value)
      case 'name':
      default:
        return countries.find((opt) => opt.name === value)
    }
  }, [value, countries, valueType])

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country) => {
      onChange(country)
      setIsOpen(false)
      setSearchQuery('')
      setDisplayCount(20)
    },
    [onChange]
  )

  // Handle infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (scrollHeight - scrollTop <= clientHeight + 10) {
        setDisplayCount((prev) => Math.min(prev + 20, countries.length))
      }
    },
    [countries.length]
  )

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(20)
  }, [searchQuery])

  if (isLoadingCountries) {
    return (
      <div
        className={cn('h-10 animate-pulse rounded-md bg-muted', className)}
      />
    )
  }

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={isOpen}
          className={cn('justify-between', triggerClassName, className)}
          disabled={disabled}
          role="combobox"
          variant="outline"
        >
          <div className="flex items-center gap-2">
            {showFlag &&
              selectedOption &&
              renderFlag(selectedOption.flag, selectedOption.code)}
            {compactMode ? (
              // Compact mode: show only prefix for phone selector
              <span className="text-sm">
                {selectedOption?.prefix || placeholder}
              </span>
            ) : (
              // Normal mode: show country name and optionally prefix
              <>
                <span className="truncate">
                  {selectedOption?.name || placeholder}
                </span>
                {showPrefix && selectedOption && (
                  <span className="text-sm text-muted-foreground">
                    {selectedOption.prefix}
                  </span>
                )}
              </>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-[--radix-popover-trigger-width] p-0',
          contentClassName
        )}
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <div className="px-3 pt-3">
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-9"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              value={searchQuery}
            />
          </div>
        </div>
        <div className="max-h-[250px] overflow-y-auto" onScroll={handleScroll}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                className="cursor-pointer px-3 py-2 hover:bg-muted"
                key={option.code}
                onClick={() => handleCountrySelect(option)}
              >
                <div className="flex items-center gap-3">
                  {renderFlag(option.flag, option.code)}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {option.name}
                    </div>
                    {showPrefix && (
                      <div className="text-xs text-muted-foreground">
                        {option.prefix}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No se encontraron países
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
